import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Key, Ban, Trash2, CheckCircle, XCircle, AlertTriangle, Shield, User, UserPlus } from 'lucide-react';

export default function UserSettings() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetModal, setResetModal] = useState({ open: false, user: null });
  const [newPassword, setNewPassword] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });
  const [createModal, setCreateModal] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ name: '', email: '', password: '', role: 'broker' });
  const [saving, setSaving] = useState(false);

  const loadUsers = async () => {
    try {
      const data = await api.get('/admin/users');
      setUsers(data);
    } catch (e) {
      toast.error('Error al cargar usuarios');
    }
    setLoading(false);
  };

  useEffect(() => { loadUsers(); }, []);

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      return toast.error('La contraseña debe tener al menos 6 caracteres');
    }
    setSaving(true);
    try {
      await api.put(`/admin/users/${resetModal.user.id}/reset-password`, { new_password: newPassword });
      toast.success(`Contraseña de ${resetModal.user.name} actualizada`);
      setResetModal({ open: false, user: null });
      setNewPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al resetear contraseña');
    }
    setSaving(false);
  };

  const handleToggleStatus = async (targetUser) => {
    try {
      const res = await api.put(`/admin/users/${targetUser.id}/toggle-status`);
      toast.success(res.message);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleDeleteUser = async () => {
    setSaving(true);
    try {
      await api.del(`/admin/users/${deleteModal.user.id}`);
      toast.success(`Usuario ${deleteModal.user.name} eliminado permanentemente`);
      setDeleteModal({ open: false, user: null });
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar usuario');
    }
    setSaving(false);
  };

  const handleCreateUser = async () => {
    if (!newUserForm.name || !newUserForm.email || !newUserForm.password) {
      return toast.error('Todos los campos son obligatorios');
    }
    if (newUserForm.password.length < 6) {
      return toast.error('La contraseña debe tener al menos 6 caracteres');
    }
    setSaving(true);
    try {
      await api.post('/admin/users', newUserForm);
      toast.success(`Usuario ${newUserForm.name} creado exitosamente`);
      setCreateModal(false);
      setNewUserForm({ name: '', email: '', password: '', role: 'broker' });
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear usuario');
    }
    setSaving(false);
  };

  if (user?.role !== 'admin') {
    return <div className="p-8 text-center text-white/50">Solo para administradores.</div>;
  }

  const inputCls = "bg-[#0A1F1A] border-[#1A3B32] focus:border-[#A38560] focus:ring-1 focus:ring-[#A38560] h-11 text-white placeholder:text-white/30 rounded-xl";

  return (
    <div data-testid="user-settings-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl lg:text-3xl font-medium tracking-tight text-white">Configuracion de Usuarios</h1>
          <p className="text-sm text-white/50 mt-1">Gestiona los miembros del equipo</p>
        </div>
        <button
          onClick={() => setCreateModal(true)}
          data-testid="create-user-btn"
          className="flex items-center gap-2 bg-gradient-to-r from-[#A38560] to-[#8B6F47] hover:from-[#8B6F47] hover:to-[#725A3A] text-[#03110D] font-semibold px-5 py-2.5 rounded-xl shadow-lg shadow-[#A38560]/20 transition-all active:scale-[0.98]"
        >
          <UserPlus size={18} /> Crear Usuario
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse font-outfit text-lg text-white/50">Cargando usuarios...</div>
        </div>
      ) : (
        <div className="space-y-4">
          {users.map(u => (
            <div key={u.id} data-testid={`user-card-${u.id}`}
              className={`bg-[#0A1F1A] rounded-2xl border shadow-sm p-5 transition-all ${
                u.is_active === false ? 'border-red-500/30 bg-red-500/5' : 'border-white/5 hover:border-[#A38560]/30'
              }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold ${
                    u.role === 'admin' ? 'bg-gradient-to-br from-[#A38560] to-[#8B6F47] text-[#03110D]' : 'bg-[#16302B] text-white border border-white/10'
                  }`}>
                    {u.name?.charAt(0)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-outfit text-lg font-medium text-white">{u.name}</p>
                      {u.role === 'admin' ? (
                        <Badge className="bg-[#A38560]/10 text-[#A38560] border-0 text-xs gap-1">
                          <Shield size={10} /> Admin
                        </Badge>
                      ) : (
                        <Badge className="bg-white/5 text-white/60 border-0 text-xs gap-1">
                          <User size={10} /> Broker
                        </Badge>
                      )}
                      {u.is_active === false && (
                        <Badge className="bg-red-500/10 text-red-400 border-0 text-xs gap-1">
                          <XCircle size={10} /> Inhabilitado
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-white/40">{u.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* Reset Password Button */}
                  <button
                    onClick={() => { setResetModal({ open: true, user: u }); setNewPassword(''); }}
                    data-testid={`reset-pwd-btn-${u.id}`}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white/60 bg-[#16302B] hover:bg-[#1A3B32] rounded-lg transition-colors border border-white/5"
                  >
                    <Key size={14} /> Nueva Clave
                  </button>

                  {/* Toggle Status Button (only for brokers) */}
                  {u.role !== 'admin' && (
                    <button
                      onClick={() => handleToggleStatus(u)}
                      data-testid={`toggle-status-btn-${u.id}`}
                      className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-colors border ${
                        u.is_active === false
                          ? 'text-[#6BCB77] bg-[#6BCB77]/10 border-[#6BCB77]/20 hover:bg-[#6BCB77]/20'
                          : 'text-amber-400 bg-amber-500/10 border-amber-500/20 hover:bg-amber-500/20'
                      }`}
                    >
                      {u.is_active === false ? (
                        <><CheckCircle size={14} /> Habilitar</>
                      ) : (
                        <><Ban size={14} /> Inhabilitar</>
                      )}
                    </button>
                  )}

                  {/* Delete Button (only for brokers) */}
                  {u.role !== 'admin' && (
                    <button
                      onClick={() => setDeleteModal({ open: true, user: u })}
                      data-testid={`delete-btn-${u.id}`}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-colors border border-red-500/20"
                    >
                      <Trash2 size={14} /> Eliminar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reset Password Modal */}
      <Dialog open={resetModal.open} onOpenChange={(open) => { setResetModal({ open, user: open ? resetModal.user : null }); setNewPassword(''); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Key size={20} className="text-[#A38560]" />
              Resetear Contraseña
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-white/50">
              Establecer nueva contraseña para <span className="font-medium text-white">{resetModal.user?.name}</span>
            </p>
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Nueva Contraseña</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Minimo 6 caracteres"
                className={inputCls}
                data-testid="new-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setResetModal({ open: false, user: null })}
              className="px-4 py-2 text-sm font-medium text-white/50 hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleResetPassword}
              disabled={saving}
              data-testid="confirm-reset-pwd-btn"
              className="bg-[#A38560] hover:bg-[#8B6F47] text-[#03110D] font-medium px-4 py-2 rounded-md shadow-sm transition-all disabled:opacity-50"
            >
              {saving ? 'Guardando...' : 'Guardar Contraseña'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, user: open ? deleteModal.user : null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2 text-red-600">
              <AlertTriangle size={20} />
              Eliminar Usuario Permanentemente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-white/50">
              ¿Estas seguro de que deseas eliminar a <span className="font-medium text-white">{deleteModal.user?.name}</span>?
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
              <p className="font-medium flex items-center gap-2"><AlertTriangle size={14} /> Esta accion no se puede deshacer</p>
              <p className="mt-1 text-xs">Los negocios creados por este usuario se conservaran en el sistema.</p>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setDeleteModal({ open: false, user: null })}
              className="px-4 py-2 text-sm font-medium text-white/50 hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleDeleteUser}
              disabled={saving}
              data-testid="confirm-delete-btn"
              className="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-md shadow-sm transition-all disabled:opacity-50"
            >
              {saving ? 'Eliminando...' : 'Eliminar Permanentemente'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Modal */}
      <Dialog open={createModal} onOpenChange={(open) => { setCreateModal(open); if (!open) setNewUserForm({ name: '', email: '', password: '', role: 'broker' }); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <UserPlus size={20} className="text-[#A38560]" />
              Crear Nuevo Usuario
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Nombre Completo *</Label>
              <Input
                value={newUserForm.name}
                onChange={e => setNewUserForm({ ...newUserForm, name: e.target.value })}
                placeholder="Nombre del usuario"
                className={inputCls}
                data-testid="create-user-name"
              />
            </div>
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Correo Electronico *</Label>
              <Input
                type="email"
                value={newUserForm.email}
                onChange={e => setNewUserForm({ ...newUserForm, email: e.target.value })}
                placeholder="correo@ejemplo.com"
                className={inputCls}
                data-testid="create-user-email"
              />
            </div>
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Contraseña *</Label>
              <Input
                type="password"
                value={newUserForm.password}
                onChange={e => setNewUserForm({ ...newUserForm, password: e.target.value })}
                placeholder="Minimo 6 caracteres"
                className={inputCls}
                data-testid="create-user-password"
              />
            </div>
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Rol</Label>
              <Select value={newUserForm.role} onValueChange={v => setNewUserForm({ ...newUserForm, role: v })}>
                <SelectTrigger data-testid="create-user-role" className={inputCls}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="broker">Broker</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => { setCreateModal(false); setNewUserForm({ name: '', email: '', password: '', role: 'broker' }); }}
              className="px-4 py-2 text-sm font-medium text-white/50 hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleCreateUser}
              disabled={saving}
              data-testid="confirm-create-user-btn"
              className="bg-[#A38560] hover:bg-[#8B6F47] text-[#03110D] font-medium px-4 py-2 rounded-md shadow-sm transition-all disabled:opacity-50"
            >
              {saving ? 'Creando...' : 'Crear Usuario'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
